import {
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  onSnapshot,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  DocumentSnapshot,
  QueryDocumentSnapshot,
  increment,
  Unsubscribe,
  runTransaction,
} from 'firebase/firestore';
import { firestore } from '../utils/firebaseSetUp';
import { Order, OrderCreate } from '../models/order';
import { isSelfHosted } from '../config/infra';
import { authenticatedHttpClient } from './httpClient';
import { getOrderServer } from './ordersServer';
import {
  createApiListCursor,
  getApiListOffset,
} from '../utils/apiListCursor';
import { createPollingSubscription } from '../utils/pollingSubscription';
import { sortOrdersByCreatedThenSaleNumber } from '../utils/orderListSort';

export type { Order };

export interface OrdersQueryResult {
  orders: Order[];
  lastDoc: QueryDocumentSnapshot | null;
  hasMore: boolean;
}

interface ProfitSyncEntitySyncState {
  profitPlus2k8?: { syncedAt?: unknown };
}

function hasProfitPlusSyncedAt(value: unknown): boolean {
  if (value == null) return false;
  if (value instanceof Date) return Number.isFinite(value.getTime());
  if (
    typeof value === 'object' &&
    value != null &&
    typeof (value as { toDate?: () => Date }).toDate === 'function'
  ) {
    const d = (value as { toDate: () => Date }).toDate();
    return d instanceof Date && Number.isFinite(d.getTime());
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    return Number.isFinite(d.getTime());
  }
  return false;
}

export interface ProfitOrderSyncEligibilityInput {
  clientCode?: string;
  fallbackClientId?: string;
  productCodes: string[];
  /**
   * MayorX sales person id (`salesPeople/{id}`). Ignored for `admin`.
   * When `salesPersonCoVenOverride` is set, the sales-person doc sync check is skipped.
   */
  fallbackSalesPersonId?: string;
  /** Explicit ERP `co_ven` on the sale — skips sales-person document sync check. */
  salesPersonCoVenOverride?: string;
}

export interface ProfitOrderSyncEligibilityResult {
  resolvedClientCode?: string;
  issues: string[];
}

export interface ProfitOrderSyncAssessment {
  blockers: string[];
  warnings: string[];
}

export interface ProfitOrderSyncEntityAssessment {
  resolvedClientCode?: string;
  blockers: string[];
  warnings: string[];
}

/**
 * Validates Profit order-sync prerequisites by ERP/business codes:
 * - client by `code`
 * - products by `code`
 * - optional: sales person doc (unless `salesPersonCoVenOverride` is set)
 * - each record must already have `profitPlus2k8.syncedAt` where applicable
 */
export async function validateOrderProfitSyncEligibility(
  organizationId: string,
  input: ProfitOrderSyncEligibilityInput
): Promise<ProfitOrderSyncEligibilityResult> {
  const assessment = await assessOrderProfitSyncEntityEligibility(
    organizationId,
    input
  );
  return {
    resolvedClientCode: assessment.resolvedClientCode,
    issues: [...assessment.blockers, ...assessment.warnings],
  };
}

export async function assessOrderProfitSyncEntityEligibility(
  organizationId: string,
  input: ProfitOrderSyncEligibilityInput
): Promise<ProfitOrderSyncEntityAssessment> {
  const issues: string[] = [];
  const warnings: string[] = [];

  let resolvedClientCode = String(input.clientCode || '').trim();
  const fallbackClientId = String(input.fallbackClientId || '').trim();

  if (!resolvedClientCode && fallbackClientId) {
    try {
      const fallbackClientRef = doc(
        firestore,
        'organizations',
        organizationId,
        'clients',
        fallbackClientId
      );
      const fallbackClientSnap = await getDoc(fallbackClientRef);
      if (fallbackClientSnap.exists()) {
        resolvedClientCode = String(fallbackClientSnap.data()?.code || '').trim();
      }
    } catch (e) {
      console.error('Could not resolve client code from client id', e);
    }
  }

  if (!resolvedClientCode) {
    issues.push('no se pudo determinar el código del cliente de la venta');
  } else {
    try {
      const clientsRef = collection(
        firestore,
        'organizations',
        organizationId,
        'clients'
      );
      const byCode = query(
        clientsRef,
        where('code', '==', resolvedClientCode),
        limit(1)
      );
      const clientSnap = await getDocs(byCode);
      if (clientSnap.empty) {
        issues.push(`no existe un cliente con código ${resolvedClientCode}`);
      } else {
        const clientData = clientSnap.docs[0].data() as ProfitSyncEntitySyncState;
        if (!hasProfitPlusSyncedAt(clientData?.profitPlus2k8?.syncedAt)) {
          issues.push(
            `el cliente con código ${resolvedClientCode} aún no está sincronizado con Profit Plus`
          );
        }
      }
    } catch (e) {
      console.error('Could not verify client sync status by code', e);
      issues.push('no se pudo verificar la sincronización del cliente por código');
    }
  }

  const uniqueProductCodes = Array.from(
    new Set(input.productCodes.map(code => String(code || '').trim()).filter(Boolean))
  );
  if (uniqueProductCodes.length === 0) {
    issues.push('la venta no tiene productos con código para sincronizar');
  } else {
    try {
      const productsRef = collection(
        firestore,
        'organizations',
        organizationId,
        'products'
      );
      const productChecks = await Promise.all(
        uniqueProductCodes.map(async code => {
          const byCode = query(productsRef, where('code', '==', code), limit(1));
          const snap = await getDocs(byCode);
          if (snap.empty) {
            return `no existe un producto con código ${code}`;
          }
          const productData = snap.docs[0].data() as ProfitSyncEntitySyncState;
          if (!hasProfitPlusSyncedAt(productData?.profitPlus2k8?.syncedAt)) {
            return `el producto ${code} aún no está sincronizado con Profit Plus`;
          }
          return '';
        })
      );
      issues.push(...productChecks.filter(Boolean));
    } catch (e) {
      console.error('Could not verify product sync status by code', e);
      issues.push('no se pudo verificar la sincronización de los productos por código');
    }
  }

  const coVenOverride = String(input.salesPersonCoVenOverride || '').trim();
  const spId = String(input.fallbackSalesPersonId || '').trim();
  if (!coVenOverride && spId && spId !== 'admin') {
    try {
      const spRef = doc(
        firestore,
        'organizations',
        organizationId,
        'salesPeople',
        spId
      );
      const spSnap = await getDoc(spRef);
      if (!spSnap.exists()) {
        warnings.push(
          'no se encontró el vendedor seleccionado para verificar Profit Plus'
        );
      } else {
        const spData = spSnap.data() as ProfitSyncEntitySyncState;
        if (!hasProfitPlusSyncedAt(spData?.profitPlus2k8?.syncedAt)) {
          warnings.push(
            'el vendedor seleccionado aún no está sincronizado con Profit Plus (exporte el vendedor o indique «Vendedor ERP (co_ven)» en la venta)'
          );
        }
      }
    } catch (e) {
      console.error('Could not verify sales person sync status', e);
      warnings.push(
        'no se pudo verificar la sincronización del vendedor con Profit Plus'
      );
    }
  }

  return {
    resolvedClientCode: resolvedClientCode || undefined,
    blockers: issues,
    warnings,
  };
}

/**
 * Each sale line must carry a Profit sub-almacén (`co_sub` or legacy `co_alma`) for export.
 */
export function collectProfitOrderLineCoSubIssues(order: Order): string[] {
  const issues: string[] = [];
  for (const p of order.products ?? []) {
    const f = (p.profitPlus2k8?.fields ?? {}) as Record<string, unknown>;
    const sub = String(f.co_sub ?? f.co_alma ?? '').trim();
    if (!sub) {
      issues.push(
        `línea sin subalmacén (${String(p.code || '').trim() || p.name || p.productId})`
      );
    }
  }
  return issues;
}

/**
 * Required dbo.pedidos-style fields on the sale document before Profit export (local checks only).
 */
export function collectProfitOrderRequiredFieldIssues(order: Order): string[] {
  const issues: string[] = [];
  const fields = (order.profitPlus2k8?.fields ?? {}) as Record<string, unknown>;
  const requiredOrderFields: Array<keyof typeof fields | 'tasa'> = [
    'co_sucu',
    'moneda',
    'co_tran',
    'forma_pag',
    'tasa',
  ];
  const missingOrderFields: string[] = [];
  for (const key of requiredOrderFields) {
    if (key === 'tasa') {
      if (!Number.isFinite(Number(fields.tasa))) {
        missingOrderFields.push('tasa');
      }
      continue;
    }
    if (!String(fields[key] ?? '').trim()) {
      missingOrderFields.push(String(key));
    }
  }
  if (missingOrderFields.length > 0) {
    issues.push(
      `faltan campos Profit Plus obligatorios de la venta (${missingOrderFields.join(', ')})`
    );
  }
  return issues;
}

export function collectProfitOrderLineShapeIssues(order: Order): string[] {
  const issues: string[] = [];
  if (!String(order.id ?? '').trim()) {
    issues.push('no se pudo determinar el ID de la venta');
  }
  if (!String(order.clientId ?? '').trim()) {
    issues.push('la venta no tiene cliente asignado');
  }
  if (!Array.isArray(order.products) || order.products.length === 0) {
    issues.push('la venta no tiene productos para exportar');
    return issues;
  }
  for (const p of order.products) {
    const label = String(p.code || '').trim() || p.name || p.productId;
    if (!String(p.code || '').trim()) {
      issues.push(`línea sin código de producto (${label})`);
    }
    const qty = Number(p.quantity);
    if (!Number.isFinite(qty) || qty <= 0) {
      issues.push(`línea con cantidad inválida (${label})`);
    }
    const price = Number(p.price);
    if (!Number.isFinite(price) || price < 0) {
      issues.push(`línea con precio inválido (${label})`);
    }
  }
  return issues;
}

export async function collectProfitOrderSyncAssessment(
  organizationId: string,
  order: Order
): Promise<ProfitOrderSyncAssessment> {
  const blockers: string[] = [
    ...collectProfitOrderLineShapeIssues(order),
    ...collectProfitOrderRequiredFieldIssues(order),
  ];
  const warnings: string[] = [
    ...collectProfitOrderLineCoSubIssues(order),
  ];
  const orderFields = (order.profitPlus2k8?.fields ?? {}) as Record<string, unknown>;
  const eligibility = await assessOrderProfitSyncEntityEligibility(organizationId, {
    clientCode: String(orderFields.co_cli ?? '').trim() || undefined,
    fallbackClientId: String(order.clientId || '').trim() || undefined,
    productCodes: (order.products ?? []).map(p => String(p.code || '').trim()),
    salesPersonCoVenOverride: String(orderFields.co_ven ?? '').trim() || undefined,
  });
  blockers.push(...eligibility.blockers);
  warnings.push(...eligibility.warnings);
  return { blockers, warnings };
}

/**
 * Full Profit export hard blockers. Warning-grade issues (salesperson sync and subalmacén)
 * are returned by collectProfitOrderSyncAssessment.
 */
export async function collectFullProfitOrderSyncIssues(
  organizationId: string,
  order: Order
): Promise<string[]> {
  const assessment = await collectProfitOrderSyncAssessment(organizationId, order);
  return assessment.blockers;
}

async function fetchOrdersPageSelfHosted(
  organizationId: string,
  searchInput: string | undefined,
  lastOrder: DocumentSnapshot | undefined,
  limitCount: number
): Promise<OrdersQueryResult> {
  const offset = getApiListOffset(lastOrder, 0);
  const params = new URLSearchParams();
  params.set('limit', String(limitCount));
  params.set('offset', String(offset));
  if (searchInput?.trim()) {
    params.set('search_term', searchInput.trim().toLowerCase());
  }
  const result = await authenticatedHttpClient.get<{
    orders: Order[];
    hasMore?: boolean;
  }>(`/orders/${organizationId}?${params.toString()}`);
  const hasMore = result.hasMore ?? result.orders.length >= limitCount;
  return {
    orders: sortOrdersByCreatedThenSaleNumber(result.orders),
    hasMore,
    lastDoc: hasMore
      ? (createApiListCursor(offset + limitCount) as unknown as QueryDocumentSnapshot)
      : null,
  };
}

/**
 * Load soft-deleted orders via the server API (trash bin).
 */
export const loadDeletedOrders = async (
  organizationId: string,
  searchInput?: string,
  lastOrder?: DocumentSnapshot,
  limitCount: number = 10
): Promise<OrdersQueryResult> => {
  const offset = getApiListOffset(lastOrder, 0);
  const params = new URLSearchParams();
  params.set('limit', String(limitCount));
  params.set('offset', String(offset));
  params.set('deleted_only', 'true');
  if (searchInput?.trim()) {
    params.set('search_term', searchInput.trim().toLowerCase());
  }
  const result = await authenticatedHttpClient.get<{
    orders: Order[];
    hasMore?: boolean;
  }>(`/orders/${organizationId}?${params.toString()}`);
  const hasMore = result.hasMore ?? result.orders.length >= limitCount;
  return {
    orders: result.orders,
    hasMore,
    lastDoc: hasMore
      ? (createApiListCursor(offset + limitCount) as unknown as QueryDocumentSnapshot)
      : null,
  };
};

/**
 * Load orders with pagination and optional search
 */
export const loadOrders = async (
  organizationId: string,
  searchInput?: string,
  lastOrder?: DocumentSnapshot,
  limitCount: number = 10,
  invoicedFilter?: boolean
): Promise<OrdersQueryResult> => {
  if (isSelfHosted) {
    return fetchOrdersPageSelfHosted(
      organizationId,
      searchInput,
      lastOrder,
      limitCount
    );
  }

  let baseQuery = collection(
    firestore,
    'organizations',
    organizationId,
    'sales'
  );

  // Build query conditions
  const conditions = [
    where('deleted', '==', false), // Always filter out deleted orders
  ];

  if (typeof invoicedFilter === 'boolean') {
    // For backward compatibility: include orders where 'invoiced' is NOT true if invoicedFilter is false
    if (invoicedFilter === false) {
      // Show orders where invoiced === false OR invoiced field is missing (undefined)
      conditions.push(
        where('invoiced', 'in', [false, undefined]) // null covers undefined in Firestore queries
      );
    } else {
      // Show only invoiced === true (should not hit this branch as per logic above, but keep for completeness)
      conditions.push(where('invoiced', '==', true));
    }
  }

  // Add search filter if provided
  if (searchInput && searchInput.trim() !== '') {
    conditions.push(
      where('keywords', 'array-contains', searchInput.toLowerCase())
    );
  }

  let q = query(baseQuery, ...conditions);

  // Add ordering
  q = query(q, orderBy('created', 'desc'));

  // Add pagination
  if (lastOrder) {
    q = query(q, startAfter(lastOrder));
  }

  // Add limit
  q = query(q, limit(limitCount));

  const snapshot = await getDocs(q);

  const orders: Order[] = [];
  snapshot.forEach(doc => {
    const data = doc.data();
    orders.push({
      ...(data as Omit<Order, 'id'>),
      id: doc.id,
    });
  });

  return {
    orders: sortOrdersByCreatedThenSaleNumber(orders),
    lastDoc:
      snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null,
    hasMore: snapshot.docs.length >= limitCount,
  };
};

/** Single order fetch (e.g. polling Profit sync status after approve / retry). */
export async function fetchOrderById(
  organizationId: string,
  orderId: string
): Promise<Order | null> {
  if (isSelfHosted) {
    try {
      return await getOrderServer(organizationId, orderId);
    } catch {
      return null;
    }
  }
  const ref = doc(firestore, 'organizations', organizationId, 'sales', orderId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { ...(snap.data() as Omit<Order, 'id'>), id: snap.id };
}

/**
 * Subscribe to orders with real-time updates
 */
export const subscribeToOrders = (
  organizationId: string,
  searchInput: string,
  invoicedFilter: boolean | undefined,
  onUpdate: (
    orders: Order[],
    hasMore: boolean,
    lastDoc: QueryDocumentSnapshot | null
  ) => void,
  onError: (error: Error) => void,
  limitCount: number = 10
): Unsubscribe => {
  if (isSelfHosted) {
    return createPollingSubscription(async () => {
      try {
        const result = await fetchOrdersPageSelfHosted(
          organizationId,
          searchInput,
          undefined,
          limitCount
        );
        onUpdate(result.orders, result.hasMore, result.lastDoc);
      } catch (error) {
        onError(error instanceof Error ? error : new Error(String(error)));
      }
    });
  }

  let baseQuery = collection(
    firestore,
    'organizations',
    organizationId,
    'sales'
  );

  // Build query conditions
  const conditions = [
    where('deleted', '==', false), // Always filter out deleted orders
  ];

  if (typeof invoicedFilter === 'boolean') {
    conditions.push(where('invoiced', '==', invoicedFilter));
  }

  // Add search filter if provided
  if (searchInput && searchInput.trim() !== '') {
    conditions.push(
      where('keywords', 'array-contains', searchInput.toLowerCase())
    );
  }

  let q = query(baseQuery, ...conditions);

  // Add ordering and limit
  q = query(q, orderBy('created', 'desc'), limit(limitCount));

  return onSnapshot(
    q,
    snapshot => {
      const orders: Order[] = [];
      snapshot.forEach(doc => {
        const data = doc.data();
        orders.push({
          ...(data as Omit<Order, 'id'>),
          id: doc.id,
        });
      });

      const hasMore = snapshot.docs.length >= limitCount;
      const lastDoc =
        snapshot.docs.length > 0
          ? snapshot.docs[snapshot.docs.length - 1]
          : null;

      onUpdate(sortOrdersByCreatedThenSaleNumber(orders), hasMore, lastDoc);
    },
    onError
  );
};

/**
 * Update user state (for vendor activation/deactivation)
 */
export const updateUserState = async (
  organizationId: string,
  userId: string,
  isActive: boolean
): Promise<void> => {
  const userRef = doc(
    firestore,
    'organizations',
    organizationId,
    'users',
    userId
  );
  await updateDoc(userRef, {
    isActive: isActive,
  });
};

/**
 * Create a new order/sale
 */
export const createOrder = async (
  organizationId: string,
  data: OrderCreate & { approvalState?: 'approved' | 'pending' | 'rejected' },
  orderApprovalRequired: boolean = false
): Promise<string> => {
  const baseCollection = collection(
    firestore,
    'organizations',
    organizationId,
    'sales'
  );

  // Generate simple keywords for search
  const source = [
    data.clientName,
    data.creatorSalesPerson,
    ...(data.products?.map(p => `${p.code} ${p.name}`) || []),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  const tokens = new Set<string>();
  source
    .split(/[^a-z0-9]+/i)
    .filter(Boolean)
    .forEach(word => {
      tokens.add(word);
      for (let i = 1; i <= word.length; i++) tokens.add(word.substring(0, i));
    });

  // Determine the initial approval state based on configuration
  const initialApprovalState = orderApprovalRequired ? 'pending' : 'approved';

  return await runTransaction(firestore, async transaction => {
    const now = new Date();
    const orderRef = doc(baseCollection);

    // FIRST: Read all product data to validate availability (if needed)
    const productRefs: any[] = [];
    const productSnaps: any[] = [];

    if (!orderApprovalRequired) {
      for (const product of data.products) {
        const productRef = doc(
          firestore,
          'organizations',
          organizationId,
          'products',
          product.productId
        );

        productRefs.push(productRef);
        productSnaps.push(await transaction.get(productRef));
      }

      // Validate all products exist and have sufficient inventory
      for (let i = 0; i < data.products.length; i++) {
        const product = data.products[i];
        const productSnap = productSnaps[i];

        if (!productSnap.exists()) {
          throw new Error(`Product ${product.productId} not found`);
        }

        const productData = productSnap.data();
        const currentAvailability = productData.availability || 0;

        if (currentAvailability < product.quantity) {
          throw new Error(
            `Insufficient inventory for product ${product.name}. Available: ${currentAvailability}, Requested: ${product.quantity}`
          );
        }
      }
    }

    // SECOND: Now perform all writes
    // Create the order document
    transaction.set(orderRef, {
      ...data,
      approvalState: data.approvalState || initialApprovalState,
      keywords: Array.from(tokens),
      created: now,
      updated: now,
    });

    // If approval is not required, discount inventory immediately
    if (!orderApprovalRequired) {
      for (let i = 0; i < data.products.length; i++) {
        const productRef = productRefs[i];

        transaction.update(productRef, {
          updated: now,
          availability: increment(-data.products[i].quantity),
        });
      }
    }

    return orderRef.id;
  });
};

